import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { ListChecks, Trash2, RefreshCw, AlertTriangle, Eye, X } from "lucide-react";

const STATUS_STYLES = {
  active:  "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
  expired: "bg-amber-500/10 text-amber-300 border-amber-500/40",
  revoked: "bg-red-500/10 text-red-300 border-red-500/40",
};

function fmt(iso, lang) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(
      lang === "es" ? "es-ES" : lang === "zh" ? "zh-CN" : lang === "ko" ? "ko-KR" : "en-US",
      { dateStyle: "medium", timeStyle: "short" },
    ).format(new Date(iso));
  } catch {
    return iso;
  }
}

/**
 * Owner-only panel listing every share link the user has created.
 * Rendered inside /ice (summary mode) so the user can audit and revoke
 * access on demand.
 *
 * `refreshSignal` is bumped by the parent after a successful POST /ice/share
 * so the list refetches without a manual reload.
 */
export default function IceSharesPanel({ refreshSignal = 0 }) {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmToken, setConfirmToken] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/ice/shares");
      setItems(r.data || []);
    } catch {
      // 403 (free plan) → just keep the panel empty; the share button itself
      // triggers the paywall when the user tries to create a link.
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const askRevoke = (token) => setConfirmToken(token);

  const doRevoke = async () => {
    if (!confirmToken) return;
    setRevoking(true);
    try {
      await api.delete(`/ice/share/${confirmToken}`);
      toast.success(t("ice_share_revoked_toast"));
      setConfirmToken(null);
      await load();
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "not_found") {
        // already revoked elsewhere — just reload
        await load();
        setConfirmToken(null);
      } else if (err?.response?.status !== 403) {
        toast.error(t("ice_share_err_generic"));
      }
    } finally {
      setRevoking(false);
    }
  };

  return (
    <section
      className="glass-card rounded-md p-5 print:hidden"
      data-testid="ice-shares-panel"
    >
      <div className="flex items-center justify-between gap-2 pb-3 mb-3 border-b border-gold-subtle">
        <div className="flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-gold" />
          <h2 className="font-display text-lg text-gold-light">
            {t("ice_shares_panel_title")}
          </h2>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-faded hover:text-gold-light transition"
          aria-label={t("refresh") || "refresh"}
          data-testid="ice-shares-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading && items.length === 0 ? (
        <p className="text-faded text-sm">{t("loading") || "…"}</p>
      ) : items.length === 0 ? (
        <p className="text-faded text-sm italic">{t("ice_shares_empty")}</p>
      ) : (
        <ul className="space-y-3" data-testid="ice-shares-list">
          {items.map((it) => {
            const statusCls = STATUS_STYLES[it.status] || STATUS_STYLES.active;
            const isRevoked = it.status === "revoked";
            const isExpired = it.status === "expired";
            return (
              <li
                key={it.token}
                className="border border-gold-subtle rounded-md p-3 bg-white/[0.02]"
                data-testid={`ice-share-row-${it.token}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-gold-light text-sm break-all">
                        {it.executor_email}
                      </span>
                      <span
                        className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${statusCls}`}
                        data-testid={`ice-share-status-${it.token}`}
                      >
                        {t(`ice_share_status_${it.status}`)}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5 text-[11px]">
                      <div>
                        <div className="font-label text-faded">{t("ice_shares_col_sent")}</div>
                        <div className="text-gold-light">{fmt(it.created_at, lang)}</div>
                      </div>
                      <div>
                        <div className="font-label text-faded">{t("ice_shares_col_expires")}</div>
                        <div className="text-gold-light">{fmt(it.expires_at, lang)}</div>
                      </div>
                      <div>
                        <div className="font-label text-faded">{t("ice_shares_col_views")}</div>
                        <div className="text-gold-light flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {it.view_count}
                        </div>
                      </div>
                      <div>
                        <div className="font-label text-faded">{t("ice_shares_col_failed")}</div>
                        <div className={it.failed_attempts > 0 ? "text-amber-300" : "text-gold-light"}>
                          {it.failed_attempts}
                        </div>
                      </div>
                    </div>
                  </div>
                  {!isRevoked && (
                    <button
                      type="button"
                      onClick={() => askRevoke(it.token)}
                      className="text-xs px-3 py-1.5 rounded-md border border-red-500/40 text-red-200 hover:bg-red-500/10 transition flex items-center gap-1.5 shrink-0"
                      data-testid={`ice-share-revoke-${it.token}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> {t("ice_share_revoke")}
                    </button>
                  )}
                  {isExpired && (
                    <span className="text-[11px] text-amber-300/80 italic">
                      {t("ice_share_status_expired_hint")}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Revoke confirmation dialog */}
      {confirmToken && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          role="dialog"
          aria-modal="true"
          data-testid="ice-share-revoke-confirm"
        >
          <div className="glass-card rounded-md p-6 max-w-md w-full">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h2 className="font-display text-xl text-gold-light">
                {t("ice_share_revoke_title")}
              </h2>
            </div>
            <p className="text-sm text-muted leading-relaxed mb-4">
              {t("ice_share_revoke_intro")}
            </p>
            <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmToken(null)}
                disabled={revoking}
                className="btn-ghost justify-center"
                data-testid="ice-share-revoke-cancel"
              >
                <X className="w-4 h-4" /> {t("ice_share_revoke_cancel")}
              </button>
              <button
                type="button"
                onClick={doRevoke}
                disabled={revoking}
                className="btn-gold justify-center"
                data-testid="ice-share-revoke-confirm-btn"
                style={{ background: "#B91C1C", color: "#FFF" }}
              >
                <Trash2 className="w-4 h-4" />
                {revoking ? (t("saving") || "…") : t("ice_share_revoke_yes")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
